/** Geometry shared by the watch shell, scrubber and floating call window.
 * Dimensions are CSS pixels, never device pixels or transformed bounding sizes. */
export interface WatchGeometry {
  width: number; height: number; left: number; top: number;
  logicalWidth: number; logicalHeight: number; sideways: boolean;
}

export function watchGeometry(width: number, height: number, touch: boolean,
  left = 0, top = 0, portrait = height > width): WatchGeometry {
  const sideways = touch && portrait;
  return { width, height, left, top, sideways,
    logicalWidth: sideways ? height : width, logicalHeight: sideways ? width : height };
}

export function readWatchGeometry(touch: boolean): WatchGeometry {
  return sampleWatchGeometry(touch);
}

function sampleWatchGeometry(touch: boolean, previous?: WatchGeometry): WatchGeometry {
  if (typeof window === 'undefined') return watchGeometry(1280, 720, false);
  const view = window.visualViewport;
  const width = view?.width ?? window.innerWidth;
  const height = view?.height ?? window.innerHeight;
  // Safari updates the layout and visual viewports in separate events, in either
  // order. Never apply the new layout orientation to an old visible rectangle.
  // When their widths agree (allowing CSS-pixel rounding and pinch zoom), layout
  // orientation distinguishes a keyboard-shortened portrait view from rotation.
  const sameWidth = Math.abs(width * (view?.scale ?? 1) - window.innerWidth) <= 1;
  // Keep the last coherent publication until both viewports agree. A visual-first
  // rotation must not rotate the shell before the layout viewport catches up, and
  // a layout-first rotation must not reinterpret the old keyboard rectangle.
  // Height and offsets can change independently: they are not coherence gates.
  if (touch && previous && !sameWidth) return previous;
  const portrait = sameWidth ? window.innerHeight > window.innerWidth : height > width;
  return watchGeometry(width, height, touch, view?.offsetLeft ?? 0, view?.offsetTop ?? 0, portrait);
}

function sameGeometry(a: WatchGeometry, b: WatchGeometry): boolean {
  return a.width === b.width && a.height === b.height && a.left === b.left && a.top === b.top &&
    a.sideways === b.sideways;
}

export function observeWatchGeometry(touch: () => boolean, receive: (g: WatchGeometry) => void): () => void {
  const view = window.visualViewport;
  const media = window.matchMedia('(hover: none) and (pointer: coarse)');
  let wasTouch = touch();
  let current = readWatchGeometry(wasTouch);
  let candidateSideways: boolean | undefined;
  let frame: number | undefined;
  let disposed = false;

  const update = () => {
    if (!disposed && frame === undefined) frame = window.requestAnimationFrame(flush);
  };
  const flush = () => {
    frame = undefined;
    if (disposed) return;
    const isTouch = touch();
    const next = sampleWatchGeometry(isTouch, wasTouch === isTouch ? current : undefined);
    // An axis change must survive a frame boundary. Event bursts and a one-frame
    // portrait bounce must not rotate the entire watch UI and then rotate it back.
    // Ordinary keyboard, toolbar and offset changes still publish on the first RAF.
    if (isTouch && next.sideways !== current.sideways && candidateSideways !== next.sideways) {
      candidateSideways = next.sideways;
      update();
      return;
    }
    candidateSideways = undefined;
    wasTouch = isTouch;
    if (!sameGeometry(current, next)) {
      current = next;
      receive(next);
    }
  };

  receive(current);
  window.addEventListener('resize', update);
  window.addEventListener('orientationchange', update);
  view?.addEventListener('resize', update);
  view?.addEventListener('scroll', update);
  media.addEventListener('change', update);
  return () => {
    disposed = true;
    if (frame !== undefined) window.cancelAnimationFrame(frame);
    window.removeEventListener('resize', update);
    window.removeEventListener('orientationchange', update);
    view?.removeEventListener('resize', update);
    view?.removeEventListener('scroll', update);
    media.removeEventListener('change', update);
  };
}

export function watchSpace(node: Element | null): HTMLElement | null {
  return node?.closest?.<HTMLElement>('[data-watch-space]') ?? null;
}
export function sideways(node: Element | null): boolean {
  return watchSpace(node)?.dataset.sideways === 'true';
}

/** Inverse of the shell's clockwise quarter-turn, including visible viewport offset.
 * Exported separately so the coordinate contract can be checked without a DOM. */
export function inverseWatchPoint(x: number, y: number,
  rect: { left: number; top: number; right: number; width: number; height: number },
  width: number, height: number, rotated: boolean): { x: number; y: number } {
  const sx = (rotated ? rect.height : rect.width) / (width || 1) || 1;
  const sy = (rotated ? rect.width : rect.height) / (height || 1) || 1;
  // DOMMatrix handles the same coordinate mapping as CSS. The numeric inverse is
  // also used by non-browser unit tests and older WebKit without DOMMatrix.
  if (typeof DOMMatrix !== 'undefined') {
    const matrix = new DOMMatrix(rotated
      ? [0, sx, -sy, 0, rect.right, rect.top]
      : [sx, 0, 0, sy, rect.left, rect.top]);
    const point = matrix.inverse().transformPoint({ x, y });
    return { x: point.x, y: point.y };
  }
  return rotated ? { x: (y - rect.top) / sx, y: (rect.right - x) / sy }
    : { x: (x - rect.left) / sx, y: (y - rect.top) / sy };
}

/** Target-local coordinates, not the axis-aligned physical bounding rectangle. */
export function localPoint(node: HTMLElement, clientX: number, clientY: number): { x: number; y: number } {
  return inverseWatchPoint(clientX, clientY, node.getBoundingClientRect(),
    node.offsetWidth, node.offsetHeight, sideways(node));
}

export function seekFraction(node: HTMLElement, clientX: number, clientY: number): number {
  if (node.offsetWidth <= 0) return 0;
  return Math.max(0, Math.min(1, localPoint(node, clientX, clientY).x / node.offsetWidth));
}
